import { Box, ClickAwayListener, Popper, Typography } from "@mui/material";
import type { Instance as PopperInstance } from "@popperjs/core";
import CommonService from "../../services/common/commonService";
import { accountRatingOptions, accountStatusOptions, capitalizeFirstLetter, convertToUTC, formatClientDomain, getNormalizedTimeZone, getPresetDateRange, TimelineItem, TimelineTooltipPayload, downloadBlob, prepareExportDownload, parseBlobError,
  relationshipTypeFilterOptions,
  flightRiskOptions
 } from "../../utils/utils";
import { format } from "date-fns";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { getDefaultAccountColumns, initialSorting } from "./accountsTableConfig";
import useGlobalContext from "../../context/useGlobalContext";
import { useLocation, useNavigate } from "react-router-dom";
import useDisplaySnackbar from "../../utils/useDisplaySnackbar";
import SlideOver from "../Common/SlideOver";
import ClientInteractionActionView from "../ClientOverview/ClientInteractionActionView";
import InteractionAsk7G from "../MeetingDetailsPage/InteractionAsk7G";
import FilterPill from "../Common/FilterPill";
import ViewSwitcherPill, { ViewSwitcherOption } from "../Common/ViewSwitcherPill";
import AccountsTimeLineViewIcon from "../../assets/accounts/accounts_time_line_icon.svg";
import AccountsSummaryViewIcon from "../../assets/accounts/accounts_summary_view_icon.svg";
import { getCustomerAccountColumns, initialSort } from "./accountsCustomerTableConfig";
import Datatable from "../Common/Datatable";
import { ACCOUNT_SUB_TABS, AccountColumnVisibility, AccountViewMode, AccountViewState, Options, UserInfo, UserTeamData } from "../../utils/types";
import { ThemeCategoryKey } from "../../interfaces/themes";
import "../../styles/account.css";
import InteractionTooltip from "./interactionTooltip";
import EmailTooltip from "./EmailTooltip";
import ColumnFilter, { ExclusiveColumnFilterContext } from "../Common/customFilter";
import UserTeam from "../Analytics/UserTeam";
import { getUserAttributes } from "../../utils/auth";
import CombinedTooltip from "./CombinedTooltip";
import OrgHierarcgyIcon from "../../assets/accounts/org-hierarchy-icon.svg";
import SpaceIcon from "../../assets/accounts/spaces-icon.svg";
import DownloadReport from "../Common/DownloadReport";
import NavigationSlideOver from "../Common/NavigationSlideOver";
import FlightRiskAssessment from "../ClientOverview/flightRisk/FlightRiskAssessment";
import flagRiskIcon from "../../assets/accounts/account_flight_risk_flag_icon.svg";
import { formatDateWithSlash, buildResolvedFlightRiskPayload } from "../../utils/flightRiskUtils";
import MarkAsResolvedModal from "../ClientOverview/flightRisk/MarkAsResolvedModal";
import ClientService from "../../services/clients/ClientService";
import IntegrationService from "../../services/integrations/Integrations";
import { parseSavedViewsMap } from "../Analytics/MMNAReport";
import { useQueryClient } from "@tanstack/react-query";

const VALID_SORT_FIELDS = new Set([
  "account",
  "clientType",
  "mrr",
  "accountManagerName",
  "advertisingAccountManagerName",
]);

const ACCOUNT_REPORT_COLUMN_KEYS = [
  "account",
  "status",
  "clientType",
  "mrr",
  "rating",
  "accountManagerName",
  "flightRisk",
  "interactions",
  "email",
  "trajectory",
  "advertisingAccountManagerName",
] as const;

const toAccountExportColumns = (
  visibility: Record<string, boolean>,
  includeFlightRisk: boolean
): Record<string, boolean> =>
  Object.fromEntries(
    ACCOUNT_REPORT_COLUMN_KEYS
      .filter((key) => includeFlightRisk || key !== "flightRisk")
      .map((key) => [key, visibility[key] === true])
  );

type AccountColumnFilters = {
  account?: string[];
  rating?: string[];
  mrr?: {
    operator: "gt" | "lt";
    value: number;
  };
  clientType?: string[];
  accountManagerName?: string[];
  status?: string[];
  trajectory?: string;
  isInteractionPresent?: boolean;
  accountRelationshipType?: string;
  flightRisk?: boolean;
  advertisingAccountManagerName?: string[];
};

export interface CustomerSavedViewsState {
  salesforceReportPref: any;
  activeFilterCount: number;
  visibleColumnCount: number;
  hasUnsavedChanges: boolean;
  activeViewName: string | null;
  currentConfigSnapshot: any;
  onSelectView: (viewName: string, viewConfig: any) => void;
  onClearAllFilters: () => void;
  getFilterCount: (config: any) => number;
  getColumnCount: (config: any) => number;
}

interface AccountsProps {
  setAccountDateRange: React.Dispatch<
    React.SetStateAction<{
      preset: string;
      startDate: string;
      endDate: string;
    }>
  >;
  onCustomerSavedViewsChange?: (state: CustomerSavedViewsState | null) => void;
  onSpaceModeChange?: (isSpaceMode: boolean) => void;
}

const mapToTimelineItems = (
  interactions: {
    interactionId: string;
    title: string;
    startTime: string;
    overallSentiment?: string;
  }[] = [],
  emails:
    | Record<string, string>
    | {
        id?: string;
        messageDate?: string;
        startTime?: string;
        subject?: string;
        body?: string;
        fromEmail?: string;
        toEmail?: string[];
      }[] = {}
): TimelineItem[] => {
  const interactionItems: TimelineItem[] = interactions.map((i) => ({
    id: i.interactionId,
    start_time: i.startTime,
    type: "interaction",
    title: i.title,
    overallSentiment: i.overallSentiment,
  }));

  const emailItems: TimelineItem[] = Array.isArray(emails)
    ? emails?.map((email) => ({
        id: email.id || "",
        start_time:
          email.messageDate || email.startTime || "",
        type: "email",
        title: email.subject || "Email",
        from: email.fromEmail || "",
        to: Array.isArray(email.toEmail)
          ? email.toEmail.join(", ")
            : "",
        summary: email.body || "",
        subject: email.subject || "Email",
      }))
    : Object.entries(emails).map(([id, date]) => ({
        id,
        start_time: date,
        type: "email",
        title: "",
      }));

  return [...interactionItems, ...emailItems].sort(
    (a, b) =>
      new Date(a.start_time).getTime() -
      new Date(b.start_time).getTime()
  );
};

const generateRows = (
  accountInsights: any[] = [],
  onClientClick: (clientDomain: string, clientName: string, slideOver: boolean, orgId: string, subOrgId: string, rowRelationshipType?: string, salesforceAccountUrl?: string, salesforceAccountId?: string) => void,
  parentPageIdentifier?: string,
  onAskClick?: (clientDomain: string, orgId: string, subOrgId: string) => void,
  appliedFilters?: AccountColumnFilters,
  onFlightRiskClick?: (orgId: string, subOrgId: string, accountName?: string, isCreate?: boolean, accountManagerName?: string) => void
): any[] => {
  return accountInsights.map((item: any) => {

    return {
      name: item.clientName || item.orgName,
      domain: item.clientDomain || item.orgDomain,
      Mrr: item.mrr ?? 0,
      Email: item.primaryEmail ?? "",
      num_meetings:
        item.totalInteractions ??
        Object.keys(item.interactions || {}).length,
      num_contacts: item.totalContacts ?? 0,
      last_interaction_Date: item.lastInteractionDate ,
      last_interaction_summary:
        item?.lastThreeRatedInteractions?.[0]?.summary || "",
      lastThreeRatedInteractions: item?.lastThreeRatedInteractions || [],
      rating: item.overallRating,
      timelineInteractions: mapToTimelineItems(
        item.interactions,
        item.emails
      ),
      orgId: item?.orgId,
      subOrgId: item?.subOrgId,
      accountManagerName: item.accountManagerName,
      clientType: item.clientType,
      accountRelationshipType: item.relationshipType || "",
      monthlyScore: item.monthlyScore,
      onClientClick,
      parentPageIdentifier,
      onAskClick,
      appliedFilters,
      status: item.isAccountActive,
      relationshipTrajectory: item.relationshipTrajectory,
      relationshipTrajectoryFlag: item.relationshipTrajectoryFlag,
      flightRisk: item.isFlightRisk,
      onFlightRiskClick,
      accountSummary: item.accountSummary,
      salesforceAccountUrl: item.salesforceAccountUrl,
      salesforceAccountId: item.salesforceAccountId,
      advertisingAccountManagerName: item.advertisingAccountManagerName,
    };
  });
};

const isActiveCustomerFilterValue = (value: unknown): boolean => {
  if (value === undefined || value === null || value === "") return false;
  if (Array.isArray(value)) {
    return value.some((item) => item !== undefined && item !== null && item !== "");
  }
  if (typeof value === "object") {
    const record = value as Record<string, unknown>;
    if ("operator" in record || "value" in record) {
      return record.value !== undefined && record.value !== null && record.value !== "" && Boolean(record.operator);
    }
    return Object.values(record).some(isActiveCustomerFilterValue);
  }
  return true;
};

const asSavedFilterString = (value: unknown): string | undefined => {
  if (Array.isArray(value)) {
    const joined = value.map((item) => String(item).trim()).filter(Boolean).join(",");
    return joined || undefined;
  }
  if (typeof value === "string" && value.trim()) return value.trim();
  return undefined;
};

const asSavedFilterList = (value: unknown): string[] => {
  if (Array.isArray(value)) {
    return value.map((item) => String(item).trim()).filter(Boolean);
  }
  if (value === undefined || value === null || value === "") return [];
  return String(value).split(",").map((item) => item.trim()).filter(Boolean);
};

const columnFiltersFromSavedConfig = (config: any): AccountColumnFilters => {
  const status = asSavedFilterList(config?.status);
  const accountManagerName = asSavedFilterList(config?.account_manager);
  const rating = asSavedFilterList(config?.current_rating);
  const clientType = asSavedFilterList(config?.customer_type);
  const advertisingAccountManagerName = asSavedFilterString(config?.advertising_account_manager);

  return {
    account: asSavedFilterList(config?.customer_name),
    status: status.length > 0 ? status : undefined,
    clientType: clientType.length > 0 ? clientType : undefined,
    mrr: config?.mrr_operator && config?.mrr !== undefined && config?.mrr !== null
      ? { operator: config.mrr_operator, value: config.mrr }
      : undefined,
    rating: rating.length > 0 ? rating : undefined,
    accountManagerName: accountManagerName.length > 0 ? accountManagerName : undefined,
    flightRisk: typeof config?.flight_risk === "boolean" ? config.flight_risk : undefined,
    trajectory: asSavedFilterList(config?.trajectory)[0],
    advertisingAccountManagerName: advertisingAccountManagerName
      ? [advertisingAccountManagerName]
      : undefined,
  };
};

const CUSTOMER_ACTIVE_VIEW_KEY = "customerActiveViewName";

const buildCustomerSavedReport = ({
  columnFilters,
  columnVisibility,
  startDate,
  endDate,
  accountViewMode,
  includeFlightRisk,
}: {
  columnFilters: AccountColumnFilters;
  columnVisibility: Record<string, boolean>;
  startDate: string;
  endDate: string;
  accountViewMode?: AccountViewMode;
  includeFlightRisk: boolean;
}) => {
  const customerName = asSavedFilterString(columnFilters.account);
  const status = asSavedFilterString(columnFilters.status);
  const accountManager = asSavedFilterString(columnFilters.accountManagerName);
  const advertisingAccountManager = asSavedFilterString(columnFilters.advertisingAccountManagerName);
  const trajectory = asSavedFilterString(columnFilters.trajectory);
  const customerType = Array.isArray(columnFilters.clientType)
    ? columnFilters.clientType.filter(Boolean)
    : [];
  const currentRating = Array.isArray(columnFilters.rating)
    ? columnFilters.rating.filter(Boolean)
    : [];

  return {
    ...(customerName && { customer_name: customerName }),
    ...(status && { status }),
    ...(customerType.length > 0 && { customer_type: customerType }),
    ...(columnFilters.mrr?.operator &&
      columnFilters.mrr.value !== undefined &&
      columnFilters.mrr.value !== null && {
        mrr: columnFilters.mrr.value,
        mrr_operator: columnFilters.mrr.operator,
      }),
    ...(currentRating.length > 0 && { current_rating: currentRating }),
    ...(accountManager && { account_manager: accountManager }),
    ...(advertisingAccountManager && { advertising_account_manager: advertisingAccountManager }),
    ...(typeof columnFilters.flightRisk === "boolean" && { flight_risk: columnFilters.flightRisk }),
    ...(trajectory && { trajectory }),
    start_date: startDate,
    end_date: endDate,
    account_columns: toAccountExportColumns(columnVisibility, includeFlightRisk),
    ...(accountViewMode && { view: accountViewMode }),
  };
};

const countSavedCustomerFilters = (config: any) => {
  if (!config || typeof config !== "object") return 0;
  if (config.filters || config.dateRange) {
    const filters = config.filters;
    let count = filters && typeof filters === "object"
      ? Object.values(filters).filter(isActiveCustomerFilterValue).length
      : 0;
    if (config.dateRange?.preset && config.dateRange.preset !== "last3MonthsFull") count += 1;
    return count;
  }

  let count = 0;
  if (config.customer_name) count += 1;
  if (config.status) count += 1;
  if (Array.isArray(config.customer_type) && config.customer_type.length > 0) count += 1;
  if (config.mrr !== undefined && config.mrr !== null && config.mrr_operator) count += 1;
  if (Array.isArray(config.current_rating) && config.current_rating.length > 0) count += 1;
  if (config.account_manager) count += 1;
  if (config.advertising_account_manager) count += 1;
  if (typeof config.flight_risk === "boolean") count += 1;
  if (config.trajectory) count += 1;
  const defaultRange = getPresetDateRange("last3MonthsFull");
  if (
    config.start_date &&
    config.end_date &&
    (config.start_date !== defaultRange.start_time || config.end_date !== defaultRange.end_time)
  ) {
    count += 1;
  }
  return count;
};

const countSavedCustomerColumns = (config: any) =>
  Object.values(config?.account_columns || {}).filter(Boolean).length;

const customerViewSignature = (view: any) => JSON.stringify({
  customer_name: view?.customer_name ?? "",
  status: view?.status ?? "",
  customer_type: view?.customer_type ?? [],
  mrr: view?.mrr ?? null,
  mrr_operator: view?.mrr_operator ?? "",
  current_rating: view?.current_rating ?? [],
  account_manager: view?.account_manager ?? "",
  advertising_account_manager: view?.advertising_account_manager ?? "",
  flight_risk: typeof view?.flight_risk === "boolean" ? view.flight_risk : null,
  trajectory: view?.trajectory ?? "",
  start_date: view?.start_date ?? "",
  end_date: view?.end_date ?? "",
  account_columns: view?.account_columns ?? {},
  view: view?.view ?? "",
});

const defaultCustomerFilters = {
  account: undefined,
  mrr: undefined,
  rating: undefined,
  advertisingAccountManagerName: undefined,
};

const Accounts: React.FC<AccountsProps> = ({ setAccountDateRange, onCustomerSavedViewsChange, onSpaceModeChange }) => {
    const queryClient = useQueryClient();
    const [pageNumber, setPageNumber] = useState<number>(1);
    const [pageSize, setPageSize] = useState<number>(50);
    const [rows, setRows] = useState<any[]>([]);
    const [rowCount, setRowCount] = useState<number>(0);
    const { showSnackbar } = useDisplaySnackbar();
    const location = useLocation();
    const navigate = useNavigate();
    const [clientDomain, setClientDomain] = useState<string>(location?.state?.clientDomain);
    const [clientName, setClientName] =useState<string>(location?.state?.clientName);
    const [orgId, setOrgId] = useState<string>(location?.state?.orgId);
    const [subOrgId, setSubOrgId] = useState<string>(location?.state?.subOrgId);
    const [slideOverOpen, setSlideOverOpen] = useState(false);
    const [flightRiskSlideOverOpen, setFlightRiskSlideOverOpen] = useState(false);
    const [flightRiskTimestamp, setFlightRiskTimestamp] = useState<string | null>(null);
    const [flightRiskAccount, setFlightRiskAccount] = useState<{ orgId: string; subOrgId: string; accountName: string; isCreate?: boolean; accountManagerName?: string; } | null>(null);
    const [flightRiskHeaderActions, setFlightRiskHeaderActions] = useState<React.ReactNode>(null);
    const [isFlightRiskEditing, setIsFlightRiskEditing] = useState<boolean>(false);
    const [isStatusDropdownOpen, setIsStatusDropdownOpen] = useState<boolean>(false);
    const [isResolveModalOpen, setIsResolveModalOpen] = useState<boolean>(false);
    const [isResolving, setIsResolving] = useState<boolean>(false);
    const statusDropdownRef = useRef<HTMLDivElement>(null);
    const [selectedAccountRelationshipType, setSelectedAccountRelationshipType] = useState<string>(() => {
      const nextAccountViewState = (location?.state as any)?.accountViewState as AccountViewState | undefined;
      return (nextAccountViewState?.selectedAccountRelationshipType ?? "");
    });
    const slideOverParent = "account-parent-page";
    const incomingSlideOverParent = location.state?.slideOverParent;
    const [slideOverType, setSlideOverType] = useState<"CLIENT" | "ASK7G">("CLIENT");
    const [openFilterColumn, setOpenFilterColumn] = useState<string | null>(null);
    const {
      impersonationDetails,
      selectedView,
      setSelectedView,
      spaceList,
      selectedSpace,
      setSelectedSpace,
    } = useGlobalContext();
    const impersonatingUserPorId = impersonationDetails?.impersonatingUserPorId;
    const accessLevel = impersonationDetails?.accessType;
    const isTeamAccessLevel = impersonationDetails?.accessType === "team";
    const isImpersonating = impersonationDetails?.isImpersonating === true;
    const [openSpacesDropdown, setOpenSpacesDropdown] = useState(false);
    const [accountViewMode, setAccountViewMode] = useState<AccountViewMode>("timeline");
    const [salesforceAccountUrl, setSalesforceAccountUrl] = useState<string>((location?.state as any)?.accountViewState?.salesforceAccountUrl || "");
    const [salesforceAccountId, setSalesforceAccountId] = useState<string>((location?.state as any)?.accountViewState?.salesforceAccountId || "");

    const accountViewOptions: ViewSwitcherOption<AccountViewMode>[] = useMemo(
      () => [
        {
          label: "Timeline View",
          value: "timeline",
          icon: <img src={AccountsTimeLineViewIcon} alt="Timeline View" />,
        },
        {
          label: "Summary View",
          value: "summary",
          icon: <img src={AccountsSummaryViewIcon} alt="Summary View" />,
        },
      ],
      []
    );

    useEffect(() => {
      if (
        spaceList.length > 0 &&
        selectedView === "spaces" &&
        selectedSpace.length === 0 &&
        !openSpacesDropdown
      ) {
        setSelectedView("org");
        setOpenSpacesDropdown(false);
      }
    }, [selectedSpace, openSpacesDropdown, selectedView, spaceList]);
    useEffect(() => {
        if (incomingSlideOverParent === slideOverParent) {
          setSlideOverType("CLIENT");
          setSlideOverOpen(true);
        }
    }, [incomingSlideOverParent, location.key]);

    // Close status dropdown on outside click
    useEffect(() => {
      if (!isStatusDropdownOpen) return;
      const handleClickOutside = (e: MouseEvent) => {
        if (
          statusDropdownRef.current &&
          !statusDropdownRef.current.contains(e.target as Node)
        ) {
          setIsStatusDropdownOpen(false);
        }
      };
      document.addEventListener("mousedown", handleClickOutside);
      return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [isStatusDropdownOpen]);

    const handleConfirmResolved = async () => {
      if (!flightRiskAccount?.orgId || !flightRiskAccount?.subOrgId) {
        showSnackbar("error", "Organization information missing");
        return;
      }
      setIsResolving(true);
      try {
        await ClientService.updateFlightRiskFields(
          buildResolvedFlightRiskPayload(
            flightRiskAccount.orgId,
            flightRiskAccount.subOrgId,
          ),
        );
        showSnackbar(
          "success",
          `${capitalizeFirstLetter(flightRiskAccount?.accountName ?? "")} flight risk marked as resolved.`
        );
        await Promise.all([
          queryClient.invalidateQueries({ queryKey: ["get_flight_risk_for_client"] }),
          queryClient.invalidateQueries({ queryKey: ["get_org_summary"] }),
          queryClient.invalidateQueries({ queryKey: ["get_org_summary_space_mode"] }),
        ]);

        setIsResolveModalOpen(false);
        setIsStatusDropdownOpen(false);
        setFlightRiskSlideOverOpen(false);
        setFlightRiskTimestamp(null);
        setFlightRiskAccount(null);
        setFlightRiskHeaderActions(null);
        setIsFlightRiskEditing(false);
      } catch (err: any) {
        showSnackbar(
          "error",
          err?.response?.data?.message || err?.message || "Failed to mark flight risk as resolved"
        );
      } finally {
        setIsResolving(false);
      }
    };

    useEffect(() => {
      if (location.state?.clientDomain) {
        setClientDomain(location.state.clientDomain);
      }
      if (location.state?.orgId) {
        setOrgId(location.state.orgId);
      }
      if (location.state?.subOrgId) {
        setSubOrgId(location.state.subOrgId);
      }
      const nextAccountViewState = location.state?.accountViewState as AccountViewState | undefined;
      if (location.state?.clientDomain || location.state?.subOrgId || location.state?.orgId || nextAccountViewState) {
        setSalesforceAccountId(nextAccountViewState?.salesforceAccountId || location.state?.salesforceAccountId || "");
        setSalesforceAccountUrl(nextAccountViewState?.salesforceAccountUrl || location.state?.salesforceAccountUrl || "");
      }
      if (nextAccountViewState?.accountViewMode) {
        setAccountViewMode(nextAccountViewState.accountViewMode);
      }
      if(nextAccountViewState?.selectedView) {
        setSelectedView(nextAccountViewState.selectedView);
      }
      if (Array.isArray(nextAccountViewState?.selectedSpaces)) {
        setSelectedSpace(nextAccountViewState.selectedSpaces);
      }
      if (nextAccountViewState?.selectedUserPorOrgId) {
        setSelectedUser({
          profileOrgId: nextAccountViewState?.selectedUserPorOrgId,
        });
      }
      const restoredRelationshipType = nextAccountViewState?.selectedAccountRelationshipType
      if (restoredRelationshipType) {
        setSelectedAccountRelationshipType(restoredRelationshipType);
      }
    }, [location.state]);

    const initialThreeMonthRange = getPresetDateRange("last3MonthsFull");
    const [sort, setSort] = useState<{
        sort_field: string;
        sort_order: "asc" | "desc";
        }>({
        sort_field: "account",
        sort_order: "asc",
    });

    const { featureMap, userTeamData } = useGlobalContext();
    const newAccountPage = featureMap?.new_accounts_page === true;
    const shareWithSpaceEnabled = featureMap?.share_with_space === true;
    const exportAccountsDataEnabled = featureMap?.export_accounts_data === true;
    const isSpacesPageEnabled = featureMap?.spaces === true;
    const isFlightRiskEnabled = featureMap?.accounts_customers_flight_risk === true;
    const isAccountsThemesTabEnabled = featureMap?.accounts_themes_tab === true;
    const isAccountsViewModeEnabled = featureMap?.accounts_customers_view_toggle === true;
    const isFlightRiskResolveEnabled = featureMap?.accounts_flight_risk_resolve_option === true;
    const isFlightRiskCreateUpdateEnabled = featureMap?.accounts_flight_risk_create_update === true;

    const [dateRange, setDateRange] = useState<{
        preset: string;
        startDate: string;
        endDate: string;
    }>({
        preset: "last3MonthsFull",
        startDate: initialThreeMonthRange.start_time,
        endDate: initialThreeMonthRange.end_time,
    });

    const formatDateRange = (start: string, end: string) => {
        if (!start || !end) return "";

        const format = (d: string) =>
            new Date(d).toLocaleDateString("en-US", {
            month: "short",
            day: "2-digit",
            year: "numeric",
            });

        return `${format(start)} - ${format(end)}`;
    };

    const lastPath = useMemo(() => {
    const parts = location.pathname.split("/").filter(Boolean);
        return parts[parts.length - 1] || "";
    }, [location.pathname]);

    const relationshipType = useMemo(() => {
        return lastPath === "customer" ? "client" : lastPath;
    }, [lastPath]);

    const parentPageIdentifier = useMemo(() => {
        const validPages = ["customer", "prospect", "vendor", "spaces"];
        return validPages?.includes(lastPath) 
          ? `account-${lastPath}`
          : "account-parent-page";
      }, [lastPath]);

    const isCustomerView = lastPath === "customer";
    const isSpacesView = lastPath === "spaces";

    useEffect(() => {
      if (!isCustomerView) {
        setSelectedView("org");
      }
    }, [isCustomerView]);

    const savedFilters = sessionStorage.getItem("customerFilters");
    const [columnFilters, setColumnFilters] = useState(
      savedFilters ? JSON.parse(savedFilters) : {
        account: [],
        mrr: undefined,
        rating: undefined,
      }
    );
    const appliedIncomingFiltersRef = useRef(false);

    const isThemesEnabled = isAccountsThemesTabEnabled && parentPageIdentifier === "account-customer";
    const [initialThemeCategory, setInitialThemeCategory] =
      useState<ThemeCategoryKey | undefined>(undefined);
    const isSummaryActive = isCustomerView && accountViewMode === "summary";

    useEffect(() => {
      const hasIncomingFilterState =
        Boolean(location.state?.appliedFilters) ||
        location.state?.rating !== undefined ||
        Boolean(location.state?.mrr) ||
        Boolean(location.state?.customDateRange) ||
        Boolean(location.state?.analyticsSelectedUserPorOrgId) ||
        location.state?.isInteractionPresent !== undefined ||
        Boolean(location.state?.accountManagerName) ||
        Boolean(location.state?.showAccountManagerColumn);

      if (!hasIncomingFilterState || appliedIncomingFiltersRef.current) return;

      // Reset filters first (important)
      let newFilters: any = {
        account: [],
        rating: undefined,
        mrr: undefined,
        clientType: undefined,
        accountManagerName: undefined,
        status: undefined,
        trajectory: undefined,
        isInteractionPresent: undefined,
        advertisingAccountManagerName: undefined,
      };

      if (location.state?.appliedFilters) {
        newFilters = {
          ...newFilters,
          ...location.state.appliedFilters,
        };
      }

      if (location.state?.status) {
        newFilters.status = location.state.status;
      }

      // Apply rating if present
      if (location.state?.rating !== undefined) {
        newFilters.rating = Array.isArray(location.state.rating)
          ? location.state.rating
          : [location.state.rating];
      }

      // Apply mrr if present (No Revenue case)
      if (location.state?.mrr) {
        newFilters.mrr = location.state.mrr;
      }

      if (location.state?.isInteractionPresent !== undefined) {
        newFilters.isInteractionPresent = location.state.isInteractionPresent;
      }

      if (location.state?.accountManagerName) {
        const amVal = location.state.accountManagerName;
        newFilters.accountManagerName = Array.isArray(amVal) ? amVal : [amVal];
      }

      if (location.state?.showAccountManagerColumn || location.state?.accountManagerName) {
        setColumnVisibility((prev) => ({
          ...prev,
          accountManagerName: true,
        }));
      }

      const incomingAccountViewState = (location.state as any)?.accountViewState as AccountViewState | undefined;

      if (incomingAccountViewState?.accountRelationshipType !== undefined) {
        newFilters.accountRelationshipType = incomingAccountViewState.accountRelationshipType;
      }

      if (incomingAccountViewState?.flightRisk !== undefined) {
        newFilters.flightRisk = incomingAccountViewState.flightRisk;
      }

      if (incomingAccountViewState?.rating !== undefined && location.state?.rating === undefined) {
        newFilters.rating = Array.isArray(incomingAccountViewState.rating)
          ? incomingAccountViewState.rating
          : [incomingAccountViewState.rating];
      }

      if (incomingAccountViewState?.advertisingAccountManagerName !== undefined) {
        newFilters.advertisingAccountManagerName = incomingAccountViewState.advertisingAccountManagerName;
      }

      setColumnFilters(newFilters);

      appliedIncomingFiltersRef.current = true;
      // Clear incoming filter state so later UI actions (like closing slideover)
      // don't re-apply the original Business Overview filters.
      navigate(location.pathname, {
        replace: true,
        state: location.state
          ? {
              ...location.state,
              appliedFilters: undefined,
              rating: undefined,
              mrr: undefined,
              customDateRange: undefined,
              analyticsSelectedUserPorOrgId: undefined,
              isInteractionPresent: undefined,
              accountManagerName: undefined,
              showAccountManagerColumn: undefined,
              advertisingAccountManagerName: undefined,
              accountViewState: location.state?.accountViewState
                ? {
                    ...location.state.accountViewState,
                    rating: undefined,
                    accountRelationshipType: undefined,
                    flightRisk: undefined,
                    advertisingAccountManagerName: undefined,
                  }
                : undefined,
            }
          : null,
      });
    }, [location.state]);

    useEffect(() => {
      if (!location.state?.customDateRange) return;

      const updatedRange = {
        preset: "custom",
        startDate: location.state.customDateRange.startDate,
        endDate: location.state.customDateRange.endDate,
      };

      setDateRange(updatedRange);
      setAccountDateRange(updatedRange);
    }, [location.state?.customDateRange]);

    useEffect(() => {
      if (location.state?.customDateRange) return;
      if (lastPath === "customer" &&
          dateRange.preset !== "custom") {
        const defaultRange = getPresetDateRange("last3MonthsFull");

        const updatedRange = {
          preset: "last3MonthsFull",
          startDate: defaultRange.start_time,
          endDate: defaultRange.end_time,
        };

        setDateRange(updatedRange);
        setAccountDateRange(updatedRange);
      }
    }, [lastPath, location.state?.customDateRange]);

    // const [headerDateRange] = useState({
    //   startDate: initialThreeMonthRange.start_time,
    //   endDate: initialThreeMonthRange.end_time,
    // });

    const defaultColumnVisibility = useMemo<Record<string, boolean>>(() => ({
      num_contacts: false,
      account: true,
      status: true,
      clientType: true,
      mrr: true,
      rating: true,
      accountManagerName: false,
      flightRisk: isFlightRiskEnabled,
      interactions: true,
      email: false,
      trajectory: true,
      timeline: true,
      accountSummary: false,
      advertisingAccountManagerName: false,
    }), [isFlightRiskEnabled]);

    const [columnVisibility, setColumnVisibility] = useState<Record<string, boolean>>(() => {
      try {
        const saved = sessionStorage.getItem("customerColumnVisibility");

        return saved
          ? { ...defaultColumnVisibility, ...JSON.parse(saved), flightRisk: isFlightRiskEnabled ? (JSON.parse(saved).flightRisk ?? true) : false }
          : defaultColumnVisibility;
      } catch {
        return defaultColumnVisibility;
      }
    });

    useEffect(() => {
    const toSave = { ...columnVisibility };
    // Don't save false while the feature flag is disabled
    if (!isFlightRiskEnabled) {
      delete toSave.flightRisk;
    }
    sessionStorage.setItem("customerColumnVisibility", JSON.stringify(toSave));
  }, [columnVisibility, isFlightRiskEnabled]);
    useEffect(() => {
      const { isInteractionPresent, ...filtersToSave } = columnFilters;
      sessionStorage.setItem("customerFilters", JSON.stringify(filtersToSave));
    }, [columnFilters]);

    const locationState = location.state as {
      restoreTooltip?: boolean;
      restoreTooltipInSlideOver?: boolean;
      interactionId?: string;
      tooltipGroupDateKey?: string;
      tooltipAnchorInteractionId?: string;
      tooltipPayload?: TimelineTooltipPayload;
    } | null;
    const restoredTooltipLocationKeyRef = useRef<string | null>(null);

    const { accountDetails } = useGlobalContext();
    const loggedInEmail = getUserAttributes()?.email;
    const [newUserTeamData, setNewUserTeamData] = useState<UserTeamData | null>(null);
    const handleNewUserTeamData = (data: {
      currentUser: string;
      clickedUserInfo?: UserInfo;
      currentManager?: UserInfo;
      departments: any[];
      subordinates: UserInfo[];
      managerOptions: UserInfo[];
    }) => {
      setNewUserTeamData(data);
    };
    const loggedInProfileOrgId = accountDetails?.profileOrgId;
    const { data: customerPreferencesData } = IntegrationService.useGetUserPreferences(
      { employeePorId: loggedInProfileOrgId ?? "" },
      isCustomerView && !!loggedInProfileOrgId
    );
    const customerViewsPref = customerPreferencesData?.userPreferences?.customer_preferences;
    const [activeCustomerViewName, setActiveCustomerViewName] = useState<string | null>(() => {
      try {
        return sessionStorage.getItem(CUSTOMER_ACTIVE_VIEW_KEY);
      } catch {
        return null;
      }
    });
    const restoredActiveCustomerViewRef = useRef(false);
    const [customerViewBaseline, setCustomerViewBaseline] = useState<any>(null);
    const customerViewSnapshot = useMemo(() => buildCustomerSavedReport({
      columnFilters,
      columnVisibility,
      startDate: dateRange.startDate,
      endDate: dateRange.endDate,
      accountViewMode,
      includeFlightRisk: isFlightRiskEnabled,
    }), [columnFilters, columnVisibility, dateRange.startDate, dateRange.endDate, accountViewMode, isFlightRiskEnabled]);
    const customerActiveFilterCount = useMemo(
      () => countSavedCustomerFilters(customerViewSnapshot),
      [customerViewSnapshot]
    );
    const customerVisibleColumnCount = useMemo(
      () => countSavedCustomerColumns(customerViewSnapshot),
      [customerViewSnapshot]
    );
    const customerViewHasUnsavedChanges = useMemo(() => {
      const currentSignature = customerViewSignature(customerViewSnapshot);
      if (!customerViewBaseline) {
        const range = getPresetDateRange("last3MonthsFull");
        return currentSignature !== customerViewSignature(buildCustomerSavedReport({
          columnFilters: defaultCustomerFilters,
          columnVisibility: defaultColumnVisibility,
          startDate: range.start_time,
          endDate: range.end_time,
          accountViewMode: "timeline",
          includeFlightRisk: isFlightRiskEnabled,
        }));
      }
      const appliedViewMode = customerViewBaseline?.view === "timeline" || customerViewBaseline?.view === "summary"
        ? customerViewBaseline.view
        : accountViewMode;
      const appliedSignature = customerViewSignature(buildCustomerSavedReport({
        columnFilters: columnFiltersFromSavedConfig(customerViewBaseline),
        columnVisibility: {
          ...defaultColumnVisibility,
          ...(customerViewBaseline?.account_columns || {}),
          ...(!isFlightRiskEnabled ? { flightRisk: false } : {}),
        },
        startDate: customerViewBaseline?.start_date || dateRange.startDate,
        endDate: customerViewBaseline?.end_date || dateRange.endDate,
        accountViewMode: appliedViewMode,
        includeFlightRisk: isFlightRiskEnabled,
      }));
      return currentSignature !== appliedSignature;
    }, [accountViewMode, customerViewBaseline, customerViewSnapshot, dateRange.endDate, dateRange.startDate, defaultColumnVisibility, isFlightRiskEnabled]);
    const applyCustomerSavedView = useCallback((name: string, config: any) => {
      setActiveCustomerViewName(name);
      sessionStorage.setItem(CUSTOMER_ACTIVE_VIEW_KEY, name);
      setCustomerViewBaseline(config);
      setColumnFilters(columnFiltersFromSavedConfig(config));
      setColumnVisibility({
        ...defaultColumnVisibility,
        ...(config?.account_columns || {}),
        ...(!isFlightRiskEnabled ? { flightRisk: false } : {}),
      });
      if (config?.start_date && config?.end_date) {
        const defaultRange = getPresetDateRange("last3MonthsFull");
        const nextDateRange = {
          preset: config.start_date === defaultRange.start_time && config.end_date === defaultRange.end_time
            ? "last3MonthsFull"
            : "custom",
          startDate: config.start_date,
          endDate: config.end_date,
        };
        setDateRange(nextDateRange);
        setAccountDateRange(nextDateRange);
      }
      if (config?.view === "timeline" || config?.view === "summary") {
        setAccountViewMode(config.view);
      }
      setPageNumber(1);
    }, [defaultColumnVisibility, isFlightRiskEnabled, setAccountDateRange]);
    const clearCustomerSavedView = useCallback(() => {
      const range = getPresetDateRange("last3MonthsFull");
      const nextDateRange = {
        preset: "last3MonthsFull",
        startDate: range.start_time,
        endDate: range.end_time,
      };
      setActiveCustomerViewName(null);
      sessionStorage.removeItem(CUSTOMER_ACTIVE_VIEW_KEY);
      setCustomerViewBaseline(null);
      setColumnFilters(defaultCustomerFilters);
      setColumnVisibility(defaultColumnVisibility);
      setAccountViewMode("timeline");
      setDateRange(nextDateRange);
      setAccountDateRange(nextDateRange);
      setPageNumber(1);
    }, [defaultColumnVisibility, setAccountDateRange]);

    useEffect(() => {
      if (restoredActiveCustomerViewRef.current) return;
      if (!activeCustomerViewName) {
        restoredActiveCustomerViewRef.current = true;
        return;
      }
      if (!customerViewsPref || typeof customerViewsPref !== "object") return;

      const config = parseSavedViewsMap(customerViewsPref)[activeCustomerViewName];
      if (!config || typeof config !== "object" || Array.isArray(config)) {
        setActiveCustomerViewName(null);
        sessionStorage.removeItem(CUSTOMER_ACTIVE_VIEW_KEY);
      } else {
        applyCustomerSavedView(activeCustomerViewName, config);
      }
      restoredActiveCustomerViewRef.current = true;
    }, [customerViewsPref, activeCustomerViewName, applyCustomerSavedView]);

    useEffect(() => {
      if (!onCustomerSavedViewsChange) return;
      if (!isCustomerView) {
        onCustomerSavedViewsChange(null);
        return;
      }
      onCustomerSavedViewsChange({
        salesforceReportPref: customerViewsPref,
        activeFilterCount: customerActiveFilterCount,
        visibleColumnCount: customerVisibleColumnCount,
        hasUnsavedChanges: customerViewHasUnsavedChanges,
        activeViewName: activeCustomerViewName,
        currentConfigSnapshot: customerViewSnapshot,
        onSelectView: applyCustomerSavedView,
        onClearAllFilters: clearCustomerSavedView,
        getFilterCount: countSavedCustomerFilters,
        getColumnCount: countSavedCustomerColumns,
      });
    }, [
      onCustomerSavedViewsChange,
      isCustomerView,
      customerViewsPref,
      customerActiveFilterCount,
      customerVisibleColumnCount,
      customerViewHasUnsavedChanges,
      activeCustomerViewName,
      customerViewSnapshot,
      applyCustomerSavedView,
      clearCustomerSavedView,
    ]);

    const [selectedUser, setSelectedUser] = useState<any | null>(null); // State for selected user
    const selectedUserPorOrgId =
      selectedUser?.profileOrgId ||
      newUserTeamData?.currentUser ||
      (impersonationDetails?.isImpersonating
          ? impersonationDetails.impersonatingUserPorId
          : loggedInProfileOrgId);
          
    const accountViewState = useMemo<AccountViewState>(
      () => ({
        selectedView,
        selectedSpaces: selectedSpace,
        selectedUserPorOrgId: selectedUserPorOrgId || null,
        selectedAccountRelationshipType: selectedAccountRelationshipType || undefined,
        accountRelationshipType: columnFilters?.accountRelationshipType || undefined,
        flightRisk: columnFilters?.flightRisk !== undefined ? columnFilters.flightRisk : undefined,
        rating: columnFilters?.rating || undefined,
        accountViewMode: accountViewMode || undefined,
        advertisingAccountManagerName: columnFilters?.advertisingAccountManagerName || undefined,
        clientDomain: clientDomain || undefined,
        clientName: clientName || undefined,
        clientOrgId: orgId || undefined,
        subOrgId: subOrgId || undefined,
        slideOverParent: slideOverParent || undefined,
        parentPageIdentifier: parentPageIdentifier || undefined,
        opportunityNavigationState: (location.state as any)?.accountViewState?.opportunityNavigationState || location.state?.opportunityNavigationState,
        salesforceAccountId: salesforceAccountId || undefined,
        salesforceAccountUrl: salesforceAccountUrl || undefined,
      }),
      [selectedView, selectedSpace, selectedUserPorOrgId, selectedAccountRelationshipType, columnFilters?.accountRelationshipType, columnFilters?.flightRisk, columnFilters?.rating, columnFilters?.advertisingAccountManagerName, accountViewMode, clientDomain, clientName, orgId, subOrgId, slideOverParent, parentPageIdentifier, salesforceAccountId, salesforceAccountUrl, (location.state as any)?.accountViewState?.opportunityNavigationState, location.state?.opportunityNavigationState]
    );
    useEffect(() => {
      const analyticsSelectedUserPorOrgId = location.state?.analyticsSelectedUserPorOrgId;
      if (!analyticsSelectedUserPorOrgId) return;

      // Pre-select hierarchy user on Accounts page when coming from Analytics.
      setSelectedUser({
        profileOrgId: analyticsSelectedUserPorOrgId,
      });
      setSelectedView("org");
    }, [location.state?.analyticsSelectedUserPorOrgId]);

    const [timelineAnchorEl, setTimelineAnchorEl] = useState<HTMLElement | null>(null);
    const [activeTimelineItem, setActiveTimelineItem] = useState<TimelineTooltipPayload | null>(null);
    const [timelinePlacement, setTimelinePlacement] = useState<"top-start" | "bottom-start">("bottom-start");
    const timelinePopperRef = useRef<PopperInstance | null>(null);
    const timelineTooltipContentRef = useRef<HTMLDivElement | null>(null);
    const restoreTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const restoredTooltipPathRef = useRef<string | null>(null);

    const closeTimelineTooltip = useCallback(() => {
      if (restoreTimeoutRef.current) {
        clearTimeout(restoreTimeoutRef.current);
        restoreTimeoutRef.current = null;
      }
      restoredTooltipPathRef.current = null;
      setTimelineAnchorEl(null);
      setActiveTimelineItem(null);
    }, []);

    const onAccountSummaryTagClick = useCallback(
      (row: any, themeCategoryKey: ThemeCategoryKey) => {
        if (!isThemesEnabled) return;
        closeTimelineTooltip();
        setClientDomain(row.domain);
        setClientName(row.name);
        setOrgId(row.orgId);
        setSubOrgId(row.subOrgId);
        if (row.accountRelationshipType) {
          setSelectedAccountRelationshipType(row.accountRelationshipType);
        }
        setSalesforceAccountUrl(row.salesforceAccountUrl || "");
        setSalesforceAccountId(row.salesforceAccountId || "");
        setInitialThemeCategory(themeCategoryKey);
        setSlideOverType("CLIENT");
        setSlideOverOpen(true);
      },
      [isThemesEnabled, closeTimelineTooltip]
    );

    useEffect(() => {
      if (restoredTooltipLocationKeyRef.current === location.key) {
        return;
      }
      if (
        !locationState?.restoreTooltip ||
        locationState?.restoreTooltipInSlideOver ||
        !locationState?.interactionId ||
        !rows.length
      ) {
        return;
      }

      const tryRestore = () => {
        const tooltipPayload = locationState.tooltipPayload;
        const requestedAnchorInteractionId =
          locationState.tooltipAnchorInteractionId ||
          tooltipPayload?.anchorInteractionId ||
          locationState.interactionId;
        const requestedGroupDateKey =
          locationState.tooltipGroupDateKey || tooltipPayload?.groupDateKey;

        let clickedInteraction: TimelineItem | null = null;
        let tooltipInteractions: TimelineItem[] = [];
        let anchorInteractionId: string | null = requestedAnchorInteractionId || null;

        rows.some((row) => {
          const interactions = row.timelineInteractions || [];

          const match = interactions.find(
            (i: TimelineItem) => i.id === locationState.interactionId
          );

          if (!match) {
            return false;
          }

          clickedInteraction = match;

          const requestedIds = tooltipPayload?.interactions?.map((item) => item.id) || [];
          if (requestedIds.length) {
            tooltipInteractions = interactions.filter((item: TimelineItem) =>
              requestedIds.includes(item.id)
            );
          }

          if (!tooltipInteractions.length && requestedGroupDateKey) {
            tooltipInteractions = interactions.filter((item: TimelineItem) => {
              const dateKey = `${new Date(item.start_time).toISOString().split("T")[0]}-${item.type}`;
              return dateKey === requestedGroupDateKey;
            });
          }

          if (!tooltipInteractions.length) {
            const clickedDate = new Date(match.start_time).toDateString();
            tooltipInteractions = interactions.filter(
              (i: TimelineItem) =>
                new Date(i.start_time).toDateString() === clickedDate
            );
          }

          if (!anchorInteractionId) {
            anchorInteractionId = tooltipInteractions[0]?.id || match.id;
          }

          return true;
        });

        if (!clickedInteraction || !anchorInteractionId || !tooltipInteractions.length) return;

        const targetAnchorId = anchorInteractionId;
        const targetClickedInteraction = clickedInteraction;
        const targetTooltipInteractions = tooltipInteractions;

        let attempts = 0;
        const attemptRestore = () => {
          const el = document.querySelector(
            `[data-interaction-id="${targetAnchorId}"]`
          ) as HTMLElement | null;

          if (!el) {
            if (attempts < 10) {
              attempts++;
              restoreTimeoutRef.current = setTimeout(attemptRestore, 50);
            }
            return;
          }

          el.scrollIntoView?.({ block: "nearest", inline: "nearest" });
          setTimelineAnchorEl(el);
          const hasInteractions = targetTooltipInteractions.some(
            (item) => item.type === "interaction"
          );

          const hasEmails = targetTooltipInteractions.some(
            (item) => item.type === "email"
          );

          const restoredMode =
            hasInteractions && hasEmails
              ? "mixed"
              : targetTooltipInteractions.length > 1
              ? "group"
              : "single";

          setActiveTimelineItem({
            mode: restoredMode,
            interactions:
              targetTooltipInteractions.length > 1
                ? targetTooltipInteractions
                : targetClickedInteraction
                ? [targetClickedInteraction]
                : targetTooltipInteractions,
            emailIds: targetTooltipInteractions
              .filter((item) => item.type === "email")
              .map((item) => item.id)
              .filter(Boolean),
            navigationState: {
              ...tooltipPayload?.navigationState,
              accountViewState,
            },
            groupDateKey:
              requestedGroupDateKey ||
              `${new Date(
                targetTooltipInteractions[0].start_time
              ).toISOString().split("T")[0]}-${targetTooltipInteractions[0].type}`,
            anchorInteractionId: targetAnchorId || undefined,
          });
          restoredTooltipLocationKeyRef.current = location.key;
          restoredTooltipPathRef.current = location.pathname + location.search;

          // Clear restoreTooltip flag from React Router state
          navigate(location.pathname + location.search, {
            replace: true,
            state: {
              ...locationState,
              restoreTooltip: undefined,
              tooltipPayload: undefined,
            },
          });
        };

        attemptRestore();
      };

      tryRestore();

      return () => {
        if (restoreTimeoutRef.current) {
          clearTimeout(restoreTimeoutRef.current);
          restoreTimeoutRef.current = null;
        }
      };
    }, [location.key, location.pathname, location.search, locationState, rows, navigate, accountViewState, closeTimelineTooltip]);

    const handleClearFilters = () => {
      closeTimelineTooltip();
      const cleared = { account: [], mrr: undefined, rating: undefined, isInteractionPresent: undefined, flightRisk: undefined, advertisingAccountManagerName: undefined };
      setColumnFilters(cleared);
      sessionStorage.removeItem("customerFilters");
      setActiveCustomerViewName(null);
      setCustomerViewBaseline(null);
      sessionStorage.removeItem(CUSTOMER_ACTIVE_VIEW_KEY);
    };


  const previousAccountTabRef = useRef(lastPath);

  useEffect(() => {
    if (!ACCOUNT_SUB_TABS.includes(lastPath)) {
      return;
    }

    if (previousAccountTabRef.current !== lastPath) {
      handleClearFilters();
    }

    previousAccountTabRef.current = lastPath;
  }, [lastPath]);

    const isAccountActive =
      columnFilters.status?.[0] === "active"
        ? true
        : columnFilters.status?.[0] === "inactive"
        ? false
        : undefined;
    const canShowTeamHierarchy = isImpersonating
      ? isTeamAccessLevel
      : userTeamData?.showTeamTab;
    const showViewSwitcher =
      isCustomerView &&
      shareWithSpaceEnabled &&
      spaceList.length > 0 &&
      canShowTeamHierarchy;
    const isSpaceMode = isCustomerView && (showViewSwitcher ? selectedView === "spaces" && selectedSpace.length > 0 : selectedSpace.length > 0);
    const showRightSideFilters = isCustomerView ? newAccountPage : isSpacesView ? spaceList.length > 0 : canShowTeamHierarchy;
    useEffect(() => {
      const spaceMode = isSpacesPageEnabled && isSpacesView
    ? true
    : isSpaceMode;
    onSpaceModeChange?.(spaceMode);
    }, [isSpaceMode, onSpaceModeChange, isSpacesPageEnabled, isSpacesView]);

    const orgSummaryParams = useMemo(() => ({
        limit: pageSize,
        offset: pageNumber,
        sortOrder: sort.sort_order,
        orgName: columnFilters.account?.length
            ? columnFilters.account.join(",")
            : location?.state?.orgName,
        overallRating: (() => {
          const cleanedArray = columnFilters.rating?.filter(Boolean);
          if (!cleanedArray?.length) return undefined;
          return cleanedArray.includes("NotRated")
            ? [
                ...new Set([
                  ...cleanedArray.filter((r: any) => r !== "NotRated"),
                  "NotRated",
                  "notScorable",
                  "tooEarlyToScore",
                ]),
              ]
            : cleanedArray;
        })(),
        mrrOperator: columnFilters.mrr?.operator,
        mrrValue: columnFilters.mrr?.value,
        startTime: dateRange.startDate,
        endTime: dateRange.endDate,
        sortBy: sort.sort_field,
        employeePorId: isSpaceMode ? loggedInProfileOrgId : (selectedUserPorOrgId || ""),
        spaceIds: isSpaceMode ? selectedSpace : undefined,
        isSpaceMode,
        clientTypes: columnFilters.clientType,
        accountManagerName: Array.isArray(columnFilters.accountManagerName)
          ? columnFilters.accountManagerName.join(",")
          : columnFilters.accountManagerName,
        advertisingAccountManagerName: Array.isArray(columnFilters.advertisingAccountManagerName)
          ? columnFilters.advertisingAccountManagerName.join(",")
          : columnFilters.advertisingAccountManagerName,
        timeZone: getNormalizedTimeZone(),
        isAccountActive: isAccountActive,
        relationshipType: relationshipType,
        trajectory: Array.isArray(columnFilters.trajectory)
          ? columnFilters.trajectory[0]
          : columnFilters.trajectory,
        isFlightRisk: columnFilters.flightRisk,
        impersonatingPorId: impersonatingUserPorId,
        accessLevel: accessLevel,
        isInteractionPresent: columnFilters.isInteractionPresent,
        ...(isAccountsViewModeEnabled && {accountViewMode: (accountViewMode === "summary" ? "AccountsSummaryView" : "AccountsTimeLineView") as "AccountsSummaryView" | "AccountsTimeLineView"}),
    }), [
      pageSize,
      pageNumber,
      sort.sort_order,
      sort.sort_field,
      columnFilters,
      location?.state?.orgName,
      dateRange.startDate,
      dateRange.endDate,
      isSpaceMode,
      loggedInProfileOrgId,
      selectedUserPorOrgId,
      selectedSpace,
      isAccountActive,
      relationshipType,
      impersonatingUserPorId,
      accessLevel,
      accountViewMode,
      isAccountsViewModeEnabled
    ]);

    const {
        isLoading: isOrgSummaryLoading,
        isFetching: isOrgSummaryFetching,
        isSuccess: isOrgSummarySuccess,
        isError: isOrgSummaryError,
        error: orgSummaryError,
        data: OrgSummaryData,
    } = CommonService.useGetOrgSummary(
        orgSummaryParams,
        isCustomerView
    );

    const {
      isLoading: isOrgSummarySpacesLoading,
      isFetching: isOrgSummaySpacesFetching,
      isSuccess: isOrgSummarySpacesSuccess,
      isError: isOrgSummarySpacesError,
      error: orgSummarySpacesError,
      data: orgSummarySpacesData,
    } = CommonService.useGetOrgSummarySpaceMode({
      limit: pageSize,
      offset: pageNumber,
      startTime: dateRange.startDate,
      endTime: dateRange.endDate,
      sortOrder: sort.sort_order,
      sortBy: sort.sort_field,
      employeePorId: loggedInProfileOrgId ?? "",
      orgName: columnFilters.account?.length
            ? columnFilters.account.join(",")
            : location?.state?.orgName,
      relationshipType: columnFilters?.accountRelationshipType ?? "",
      spaceIds: selectedSpace?.length > 0 ? selectedSpace : undefined,
    }, isSpacesView && isSpacesPageEnabled)

    const {
      mutate: exportOrgSummary,
      isPending: isOrgSummaryExportPending,
    } = CommonService.useExportOrgSummary({
      onSuccess: async (data: Blob) => {
        try {
          const file = await prepareExportDownload(data);
          const start = format(new Date(dateRange.startDate), "MMM_dd_yyyy");
          const end = format(new Date(dateRange.endDate), "MMM_dd_yyyy");
          downloadBlob(file, `Customers_${start}_to_${end}.csv`);
        } catch (err: any) {
          showSnackbar("error", err.message || "Failed to download report.");
        }
      },
      onError: async (error: any) => {
        const errMsg = await parseBlobError(error);
        showSnackbar("error", errMsg);
      },
    });

    const {
      mutate: exportEmailData,
      isPending: isExportEmailDataPending,
    } = CommonService.useGetEmailDataExport({
      onSuccess: async (data: Blob) => {
        try {
          const file = await prepareExportDownload(data);
          const start = format(new Date(dateRange.startDate), "MMM_dd_yyyy");
          const end = format(new Date(dateRange.endDate), "MMM_dd_yyyy");
          downloadBlob(file, `Emails_${start}_to_${end}.csv`);
        } catch (err: any) {
          showSnackbar("error", err.message || "Failed to download report.");
        }
      },
      onError: async (error: any) => {
        const errMsg = await parseBlobError(error);
        showSnackbar("error", errMsg);
      },
    })

    const handleExportAccounts = (reportType: string = "interactions") => {
      if (!exportAccountsDataEnabled) return;
      if (reportType === "emails") {
        if (isExportEmailDataPending) return;
        exportEmailData({
          startTime: dateRange.startDate,
          endTime: dateRange.endDate,
        });
      } else {
        if (isOrgSummaryExportPending) return;
        exportOrgSummary(orgSummaryParams);
      }
    };

    const {
      isSuccess: isOrgSummaryFiltersSuccess,
      isError: isOrgSummaryFiltersError,
      error: orgSummaryFiltersError,
      data: orgSummaryFiltersData,
    } = CommonService.useGetOrgSummaryFilters(isCustomerView && newAccountPage);

    const [clientTypeOptions, setClientTypeOptions] = useState<Options[]>([]);
    const [trajectoryOptions, setTrajectoryOptions] = useState<Options[]>([]);

    useEffect(() => {
      if (isOrgSummaryFiltersSuccess && orgSummaryFiltersData) {
        const { clientTypes = [], trajectories = [] } = orgSummaryFiltersData;

        setClientTypeOptions(
          clientTypes.map((type: string) => ({
            label: capitalizeFirstLetter(type),
            value: type,
          }))
        );

        setTrajectoryOptions(
          trajectories.map((trajectory: string) => ({
            label: capitalizeFirstLetter(trajectory),
            value: trajectory,
          }))
        );
      }

      if (isOrgSummaryFiltersError) {
        showSnackbar("error", orgSummaryFiltersError?.message || "Failed to load filter options.");
      }
    }, [isOrgSummaryFiltersSuccess, orgSummaryFiltersData, isOrgSummaryFiltersError, orgSummaryFiltersError]);

    useEffect(() => {
      if (
        restoredTooltipLocationKeyRef.current === location.key ||
        restoredTooltipPathRef.current === location.pathname + location.search
      ) {
        return;
      }
      if (locationState?.restoreTooltip && !locationState?.restoreTooltipInSlideOver) {
        return;
      }
      closeTimelineTooltip();
    }, [
      location.pathname,
      location.search,
      location.key,
      locationState?.restoreTooltip,
      locationState?.restoreTooltipInSlideOver,
      closeTimelineTooltip,
    ]);

    useEffect(() => {
      if (openFilterColumn) {
        closeTimelineTooltip();
      }
    }, [openFilterColumn, closeTimelineTooltip]);

    useEffect(() => {
      closeTimelineTooltip();
    }, [dateRange.startDate, dateRange.endDate, accountViewState, closeTimelineTooltip]);

    const timelineTooltip = useMemo(
      () => ({
        anchorEl: timelineAnchorEl,
        activeItem: activeTimelineItem,
        setAnchorEl: setTimelineAnchorEl,
        setActiveItem: setActiveTimelineItem,
      }),
      [timelineAnchorEl, activeTimelineItem]
    );


    const columns = useMemo(() => {
        const handleSortChange = (field: string, order: "asc" | "desc") => {
          closeTimelineTooltip();
          if (field && VALID_SORT_FIELDS.has(field)) {
            setSort({ sort_field: field, sort_order: order });
            setPageNumber(1);
          }
        };

        return (isCustomerView && newAccountPage) || isSpacesView
            ? getCustomerAccountColumns(
                dateRange,
                columnFilters,
                setColumnFilters,
                sort,
                handleSortChange,
                timelineTooltip,
                openFilterColumn,
                setOpenFilterColumn,
                columnVisibility,
                clientTypeOptions,
                trajectoryOptions,
                isSpacesView,
                onAccountSummaryTagClick,
                isSummaryActive,
                isFlightRiskCreateUpdateEnabled
              )
            : getDefaultAccountColumns(
                columnFilters,
                setColumnFilters,
                sort,
                handleSortChange,
                openFilterColumn,
                setOpenFilterColumn
              );
    }, [
      isCustomerView,
      isSpacesView,
      newAccountPage,
      dateRange,
      columnFilters,
      sort,
      timelineTooltip,
      openFilterColumn,
      setOpenFilterColumn,
      columnVisibility,
      clientTypeOptions,
      trajectoryOptions,
      onAccountSummaryTagClick,
      isSummaryActive,
      isFlightRiskCreateUpdateEnabled,
    ]);
    
    const {
        isLoading: isAccountsLoading,
        isFetching: isAccountsFetching,
        isSuccess: isAccountsSuccess,
        isError: isAccountsError,
        error: accountsError,
        data: AccountsData,
    } = CommonService.useGetAccounts(
        {
        offset: pageNumber,
        limit: pageSize,
        sortBy: sort.sort_field,
        sortOrder: sort.sort_order,
        relationshipType,
        startTime: dateRange.startDate
        ? convertToUTC(dateRange.startDate)
        : "",
        endTime: dateRange.endDate
        ? convertToUTC(dateRange.endDate)
        : "",
        employeePorId: selectedUserPorOrgId || "",
        accountName: columnFilters.account?.length
            ? columnFilters.account.join(",")
            : location?.state?.orgName,
    },
    !(isCustomerView && newAccountPage) && !isSpacesView
    );

    useEffect(() => {
    if (isOrgSummaryError) {
        showSnackbar("error", orgSummaryError?.message || "Failed to load data.");
        }
    if (isAccountsError) {
        showSnackbar("error", accountsError?.message || "Failed to load data.");
        }   
    }, [AccountsData, isAccountsError, accountsError, relationshipType, orgSummaryError, isOrgSummaryError]);

    const activeData = (isSpacesView && isSpacesPageEnabled) ? orgSummarySpacesData :  isCustomerView && newAccountPage ? OrgSummaryData : AccountsData;
    useEffect(() => {
      const isDataSuccess =
      isSpacesView && isSpacesPageEnabled
        ? isOrgSummarySpacesSuccess
        : isCustomerView && newAccountPage
          ? isOrgSummarySuccess
          : isAccountsSuccess;
        if (!isDataSuccess || !activeData) {
          return;
        }
        const list = activeData.data || [];
        const mapped = generateRows(
          list,
          onClientClick,
          parentPageIdentifier,
          onAskClick,
          columnFilters,
          onFlightRiskClick
        );
        setRows(prev => JSON.stringify(prev) === JSON.stringify(mapped) ? prev : mapped);
        setRowCount(prev => prev === (activeData.totalRecords ?? mapped.length) ? prev : (activeData.totalRecords ?? mapped.length));
    }, [
        activeData,
        columnFilters,
        isCustomerView,
        isOrgSummarySuccess,
        isAccountsSuccess,
        parentPageIdentifier,
        isSpacesPageEnabled,
        isOrgSummarySpacesSuccess,
        isSpacesView,
        isCustomerView
    ]);
    
    useEffect(()=>{
      if(isOrgSummarySpacesError) {
        showSnackbar("error", orgSummarySpacesError?.message || "Failed to load spaces")
      }
    }, [ isOrgSummarySpacesError, orgSummarySpacesError])
    const handlePageChange = (newPage: number) => {
        closeTimelineTooltip();
        setPageNumber(newPage+1);
    };

    const handlePageSizeChange = (newPageSize: number) => {
        closeTimelineTooltip();
        setPageSize(newPageSize);
        setPageNumber(1);
    };

    const onClientClick = (clientDomain: string, clientName: string, slideOver: boolean, orgId: string, subOrgId: string, rowRelationshipType?: string, salesforceAccountUrl?: string, salesforceAccountId?: string) => {
        closeTimelineTooltip();
        setClientDomain(clientDomain);
        setClientName(clientName);
        setSlideOverOpen(slideOver);
        setOrgId(orgId);
        setSubOrgId(subOrgId);
        setInitialThemeCategory(undefined);
        if (rowRelationshipType) {
          setSelectedAccountRelationshipType(rowRelationshipType);
        }
        setSalesforceAccountUrl(salesforceAccountUrl || "");
        setSalesforceAccountId(salesforceAccountId || "");
    };
    const onAskClick = (clientDomain: string, orgId: string, subOrgId: string) => {
        closeTimelineTooltip();
        setClientDomain(clientDomain);
        setSlideOverType("ASK7G");
        setInitialThemeCategory(undefined);
        setSlideOverOpen(true);
        setOrgId(orgId);
        setSubOrgId(subOrgId);
    };

    const onFlightRiskClick = (orgId: string, subOrgId: string, accountName?: string, isCreate: boolean = false, accountManagerName?: string) => {
      if (!isFlightRiskEnabled) return;
      setFlightRiskAccount({
        orgId,
        subOrgId,
        accountName: accountName || "",
        isCreate,
        accountManagerName: accountManagerName || "",
      });
      setFlightRiskSlideOverOpen(true);
    };
    const onSortingChange = (sorting: any[]) => {
        closeTimelineTooltip();
        if (sorting && sorting.length > 0)
        setSort({
            sort_field: sorting[0].id,
            sort_order: sorting[0].desc === true ? "desc" : "asc",
        });
    };

    const updateTimelinePlacement = () => {
      if (!timelineAnchorEl) return;

      const anchorRect = timelineAnchorEl.getBoundingClientRect();
      const tooltipHeight =
        timelineTooltipContentRef.current?.getBoundingClientRect().height ?? 0;
      const viewportPadding = 16;
      const gap = 8;
      const availableBelow = window.innerHeight - anchorRect.bottom - viewportPadding - gap;
      const availableAbove = anchorRect.top - viewportPadding - gap;
      const shouldPlaceTop =
        tooltipHeight > availableBelow && availableAbove > availableBelow;

      setTimelinePlacement(shouldPlaceTop ? "top-start" : "bottom-start");
    };

    useEffect(() => {
      if (!timelineAnchorEl || !activeTimelineItem) return;

      requestAnimationFrame(() => {
        updateTimelinePlacement();
        timelinePopperRef.current?.update?.();
      });
    }, [timelineAnchorEl, activeTimelineItem]);

    useEffect(() => {
      if (!timelineTooltipContentRef.current || !activeTimelineItem) return;

      const observer = new ResizeObserver(() => {
        updateTimelinePlacement();
        timelinePopperRef.current?.update?.();
      });
      observer.observe(timelineTooltipContentRef.current);

      return () => observer.disconnect();
    }, [activeTimelineItem]);

    useEffect(() => {
      if (!timelineAnchorEl || !activeTimelineItem) return;

      const handleViewportChange = () => {
        updateTimelinePlacement();
        timelinePopperRef.current?.update?.();
      };

      window.addEventListener("resize", handleViewportChange);
      window.addEventListener("scroll", handleViewportChange, true);

      return () => {
        window.removeEventListener("resize", handleViewportChange);
        window.removeEventListener("scroll", handleViewportChange, true);
      };
    }, [timelineAnchorEl, activeTimelineItem]);

    const memoCustomDateRange = useMemo(() => ({
      startDate: dateRange.startDate,
      endDate: dateRange.endDate,
    }), [dateRange.startDate, dateRange.endDate]);

    const [pinnedColumns] = useState({
        left: ['account', 'status', 'clientType', 'mrr', 'rating', 'accountManagerName', 'flightRisk', 'trajectory', 'advertisingAccountManagerName'],
    });

    const accountPillColumns = ["account", "clientType", "mrr", "rating", "accountManagerName", "status", "trajectory",  "isInteractionPresent", "accountRelationshipType", 
      "flightRisk", "advertisingAccountManagerName"
    ] as const;
    const accountFilterLabels: Partial<Record<keyof AccountColumnFilters, string>> = {
      account: isCustomerView ? "Customer Name": "Account Name",
      mrr: "MRR",
      clientType: "Customer Type",
      accountManagerName: "Account Manager",
      status: "Status",
      rating: "Current Rating",
      isInteractionPresent: "Interactions Present",
      accountRelationshipType: "Type",
      flightRisk: "Flight Risk",
      advertisingAccountManagerName: "Advertising Account Manager",
    };

    const updateAccountColumnFilter = (
      key: keyof AccountColumnFilters,
      value: any
      ) => {
      setColumnFilters((prev: any) => ({
          ...prev,
          [key]: value,
      }));
      setPageNumber(1);
    };

    const accountFilterPills = useMemo(() => {
      return accountPillColumns.map((key) => {
        const value = columnFilters[key];
        if (
        value === undefined ||
        (Array.isArray(value) && value.length === 0)
        ) {
        return null;
        }

        let options: Options[] = [];
        let displayValue = "";

        switch (key) {
        case "account":
          options = []; // no dropdown options
          displayValue = Array.isArray(value)
              ? value.join(", ")
              : (value as string);
          break;

        case "rating":
          options = accountRatingOptions;
          displayValue =
          options.find((o) => o.value === value)?.label ||
          (value as string);
          break;
        case "mrr": {
          const mrrVal = columnFilters.mrr as { operator: "gt" | "lt"; value: number } | undefined;
          displayValue = mrrVal
            ? `${mrrVal.operator === "gt" ? "Greater than" : "Less than"} $${mrrVal.value.toLocaleString()}`
            : "";
          break;
        }
        case "clientType":
          options = clientTypeOptions;
          displayValue = Array.isArray(value)
            ? value.join(", ")
            : (value as string);
          break;
        case "accountManagerName":
          options = []; // no dropdown options
          displayValue = Array.isArray(value)
              ? value.join(", ")
              : (value as string);
          break;
        case "advertisingAccountManagerName":
          options = [];
          displayValue = Array.isArray(value)
              ? String(value[0] ?? "")
              : (value as string);
          break;

        case "accountRelationshipType":
          options = relationshipTypeFilterOptions;
          displayValue =
            relationshipTypeFilterOptions.find((o) => o.value === value)?.label ||
            (value as string);
          break;
        case "status":
          options = accountStatusOptions as unknown as Options[];
          displayValue =
            accountStatusOptions.find((o) => o.value === value)?.label || "";
          break;

        case "trajectory":
          options = trajectoryOptions;
          displayValue = trajectoryOptions.find((o) => o.value === value)?.label || "";
          break;

        case "flightRisk":
          options = flightRiskOptions.map((o) => ({
            label: o.label,
            value: String(o.value),
          })) as unknown as Options[];
          displayValue =
            flightRiskOptions.find((o) => o.value === value)?.label || "";
          break;

        case "isInteractionPresent":
          options = [];
          displayValue = "Yes";
          break;
        }

        let pillValue: any;
        if (key === "account") {
          pillValue = displayValue;
        } else if (key === "mrr") {
          pillValue = columnFilters.mrr;
        } else if (key === "clientType") {
          pillValue = Array.isArray(value) ? value : value ? [value] : [];
        } else if (key === "accountManagerName" || key === "advertisingAccountManagerName") {
          pillValue = displayValue;
        } else if (key === "status") {
          pillValue = Array.isArray(value) ? value : value ? [value] : [];
        } else if (key === "trajectory") {
          pillValue = columnFilters.trajectory;
        } else if (key === "flightRisk") {
          pillValue = typeof value === "boolean" ? [String(value)] : [];
        } else if (key === "rating") {
          pillValue = Array.isArray(value)
            ? Array.from(new Set(value.map((v) => 
                (v === "notScorable" || v === "tooEarlyToScore") ? "NotRated" : v
              )))
            : value === "notScorable" || value === "tooEarlyToScore" ? ["NotRated"] : value ? [value] : [];
        } else if (key === "isInteractionPresent") {
          pillValue = columnFilters.isInteractionPresent ? "Yes" : undefined;
        } else {
          pillValue = Array.isArray(value) ? value : value ? [String(value)] : [];
        }

        return (
          <FilterPill
            key={key}
            label={accountFilterLabels[key] ?? capitalizeFirstLetter(key)}
            type={key === "account"|| key === "accountManagerName" || key === "advertisingAccountManagerName" ? "text" : key === "mrr" ? "number" : "select"}
            options={key === "account" || key === "accountManagerName" || key === "advertisingAccountManagerName" || key === "mrr" || key === "isInteractionPresent" ? [] : options}
            value={pillValue}
            displayValue={displayValue}
            showClearIcon={true}
            readOnly={key === "isInteractionPresent"}
            parent_page="accounts_page"
            onChange={(selected) => {
              if (key === "isInteractionPresent") {
                updateAccountColumnFilter("isInteractionPresent", undefined);
                return;
              }

              if (key === "account") {
                if (!selected || !String(selected).trim()) {
                  updateAccountColumnFilter(key, undefined);
                  return;
                }
                updateAccountColumnFilter(key, [String(selected).trim()]);
                return;
              }

              if (key === "advertisingAccountManagerName") {
                const nextValue = Array.isArray(selected) ? selected[0] : selected;
                if (!nextValue || !String(nextValue).trim()) {
                  updateAccountColumnFilter(key, undefined);
                  return;
                }
                updateAccountColumnFilter(key, [String(nextValue).trim()]);
                return;
              }

              // If selected is falsy, or is an empty array, clear filter
              if (!selected || (Array.isArray(selected) && selected.length === 0)) {
                updateAccountColumnFilter(key, undefined);
                return;
              }

              if (key === "mrr") {
                const sel = Array.isArray(selected) ? selected[0] : selected;
                if (typeof sel === "object" && sel !== null && "operator" in sel && "value" in sel) {
                  updateAccountColumnFilter("mrr", sel as { operator: "gt" | "lt"; value: number });
                } else {
                  updateAccountColumnFilter("mrr", undefined);
                }
                return;
              }

              if (key === "status") {
                const val = Array.isArray(selected) ? selected : selected ? [selected] : undefined;
                updateAccountColumnFilter("status", val);
                return;
              }

              if (key === "clientType") {
                const val = Array.isArray(selected)
                  ? selected
                  : selected
                  ? [selected]
                  : undefined;

                updateAccountColumnFilter("clientType", val);
                return;
              }

              if (key === "trajectory") {
                const val = Array.isArray(selected) ? selected[0] : selected;
                updateAccountColumnFilter("trajectory", val || undefined);
                return;
              }

              if (key === "flightRisk") {
                const selectedVal = Array.isArray(selected) ? selected[0] : selected;
                const val =
                  selectedVal === "true"
                    ? true
                    : selectedVal === "false"
                    ? false
                    : typeof selectedVal === "boolean"
                    ? selectedVal
                    : undefined;
                updateAccountColumnFilter("flightRisk", val);
                return;
              }

              if (key === "rating") {
                let val = Array.isArray(selected) ? selected : selected ? [selected] : undefined;
                updateAccountColumnFilter("rating", val);
                return;
              }

              const valueToSet = Array.isArray(selected) ? selected[0] : selected;
              updateAccountColumnFilter(key, valueToSet);
            }}
          />
        );
      });
    }, [columnFilters, rows]);

    const showSpinner= 
      isAccountsLoading || isAccountsFetching ||
      isOrgSummaryLoading || isOrgSummaryFetching || isOrgSummarySpacesLoading || isOrgSummaySpacesFetching;

    const impersonatingEmployeeId =
      impersonationDetails?.accessType === "team"
        ? impersonationDetails.impersonatingUserPorId 
        : "";

    const exclusiveFilterValue = useMemo(
      () => ({
        activeKey: openFilterColumn,
        setActiveKey: setOpenFilterColumn,
      }),
      [openFilterColumn, setOpenFilterColumn]
    );

    return (
      <ExclusiveColumnFilterContext.Provider value={exclusiveFilterValue}>
        <Box sx={{width: "100%", height: "100%"}}>
            <Box className="account-page-filter-box">
            <Box className="account-page-filter-section">
            <Box className="account-page-miltiple-filter">
              {isAccountsViewModeEnabled && parentPageIdentifier === "account-customer" && (
              <ViewSwitcherPill
                value={accountViewMode}
                options={accountViewOptions}
                onChange={(val) => setAccountViewMode(val)}
              />
              )}
              <FilterPill
                key="meeting-date"
                type="date"
                label="Time Period"
                value={dateRange}
                displayValue={formatDateRange(
                    dateRange.startDate,
                    dateRange.endDate
                )}
                showClearIcon={false}
                limitFutureDays={0}
                datePresetOptions={[
                  { label: "Current Week", value: "currentWeek" },
                  { label: "Current Month", value: "currentMonth" },
                  { label: "Last Week", value: "lastWeek" },
                  { label: "Last Month", value: "lastMonth" },
                  { label: "Custom", value: "custom" },
                ]}
                onChange={(val) => {
                if (
                    val &&
                    typeof val === "object" &&
                    !Array.isArray(val) &&
                    "preset" in val
                ) {
                    // Handle preset
                    if (val.preset === "last3MonthsFull") {
                    const range = getPresetDateRange("last3MonthsFull");

                    const updated = {
                        preset: "last3MonthsFull",
                        startDate: range.start_time,
                        endDate: range.end_time,
                    };

                    setDateRange(updated);
                    setAccountDateRange(updated);
                    setPageNumber(1);
                    return;
                    }

                    setDateRange(val);
                    setAccountDateRange(val);
                    setPageNumber(1);
                }
                }}
              />
            {accountFilterPills}
            </Box>
            </Box>
            {showRightSideFilters && (
            <Box className="account-page-right-side-filters">
              <Box sx={{ flexShrink: 0 }}>
                {isSpacesView && spaceList.length > 0 ? (
                  <FilterPill
                    label="Spaces"
                    type="select"
                    value={selectedSpace}
                    options={spaceList.map((space) => ({
                      label: space.label,
                      value: space.value,
                      entityType: "space",
                    }))}
                    onChange={(value) => {
                      setOpenSpacesDropdown(false);
                      setSelectedSpace(value as string[]);
                    }}
                    showClearIcon={false}
                    forceOpen={openSpacesDropdown}
                    onOpenChange={setOpenSpacesDropdown}
                    parent_page="accounts_page"
                  />
                ) : ((!isCustomerView || selectedView === "org") && canShowTeamHierarchy) ? (
                  <UserTeam
                    setSelectedUser={setSelectedUser}
                    loggedInEmail={loggedInEmail}
                    loggedInProfileOrgId={loggedInProfileOrgId || impersonatingEmployeeId}
                    selectedEmployeePorId={ selectedUser?.profileOrgId}
                    showDropdown={true}
                    display={false}
                    onDataUpdate={handleNewUserTeamData}
                    parent_page="account-page"
                  />
                ) : spaceList.length > 0 && isCustomerView ? (
                  <FilterPill
                    label="Spaces"
                    type="select"
                    value={selectedSpace}
                    options={spaceList.map((space) => ({
                      label: space.label,
                      value: space.value,
                      entityType: "space",
                    }))}
                    onChange={(value) => {
                      if(value) {
                        setOpenSpacesDropdown(false);
                      }
                      setSelectedSpace(value as string[]);
                    }}
                    showClearIcon={false}
                    forceOpen={openSpacesDropdown}
                    onOpenChange={setOpenSpacesDropdown}
                    parent_page = "accounts_page"
                  />
                ) : null}
              </Box>
            {showViewSwitcher && isCustomerView &&
              <Box className="switch-pill">
                <Box
                  className={`org-hierarchy-switch ${
                    selectedView === "org" ? "active" : ""
                  }`}
                  onClick={() => {setSelectedView("org");}}
                >
                  <img src={OrgHierarcgyIcon}/>
                  <Typography className="switch-pill-text">Org Hierarchy</Typography>
                </Box>
                <Box
                  className={`org-hierarchy-switch ${
                    selectedView === "spaces" ? "active" : ""
                  }`}
                  onClick={() => {
                    setSelectedView("spaces");
                    if (selectedSpace.length === 0) {
                      setOpenSpacesDropdown(true);
                    }
                  }}
                >
                  <img src={SpaceIcon}/>
                  <Typography className="switch-pill-text">Spaces</Typography>
                </Box>
              </Box>
            }
            {(isCustomerView || isSpacesView) && (
            <ColumnFilter
              currentValue={columnVisibility ?? {}}
              onApply={(val) => setColumnVisibility?.(val as AccountColumnVisibility)}
              options={isSpacesView
              ? [
                  {value: "account", label: "Account", defaultValue: true},
                  {value: "accountRelationshipType", label: "Type", defaultValue: true},
                  {value: "interactions", label: "Interactions"}, 
                  {value: "email", label: "Email"}
                ]
              :[
                {value: "account", label: "Customer Name", defaultValue: true},
                {value: "status", label: "Status"},
                {value: "clientType", label: "Customer Type"},
                {value: "mrr", label: "MRR"},
                {value: "rating", label: "Rating"},
                {value: "accountManagerName", label: "Account Manager"},
                ...(isFlightRiskEnabled ? [{value: "flightRisk", label: "Flight Risk"}] : []),
                ...(!isSummaryActive
                  ? [
                      {value: "interactions", label: "Interactions"},
                      {value: "email", label: "Email"},
                    ]
                  : []),
                {value: "trajectory", label: "Trajectory"},
                {value: "advertisingAccountManagerName", label: "Advertising Account Manager"}
              ]}
              type="toggle"
              columnKey="columnVisibility"
              onOpen={() => {
                closeTimelineTooltip();
                setOpenFilterColumn("columnVisibility");
              }}
              onClose={() => setOpenFilterColumn(null)}
              showSections={{
                search: true,
                sort: false,
              }}
            />
            )}
            { isCustomerView && exportAccountsDataEnabled && (
              <DownloadReport
                onDownload={handleExportAccounts}
                options={[
                  { label: "Interactions Report", value: "interactions" },
                  { label: "Emails Report", value: "emails" },
                ]}
                disabled={isOrgSummaryExportPending || isExportEmailDataPending}
              />
            )}
            </Box>
            )}

            </Box>
            <Box
            sx={{ border: "1px solid #E5E7EB", borderRadius: "8px", overflow: "hidden", padding: "0px 0px",}}>
            <Datatable
                columns={columns}
                rows={rows}
                rowCount={rowCount}
                onPageSizeChange={handlePageSizeChange}
                onPaginationChange={handlePageChange}
                minHeight={isImpersonating ? "calc(100vh - 260px)": "calc(100vh - 230px)"}
                maxHeight={isImpersonating ? "calc(100vh - 260px)": "calc(100vh - 230px)"}
                columnVisibility={
                  isSpacesView
                    ? {
                        account: true,
                        clientType: false,
                        accountRelationshipType: true,
                        status: false,
                        mrr: false,
                        rating: false,
                        accountManagerName: false,
                        advertisingAccountManagerName: false,
                        flightRisk: false,
                        trajectory: false,
                        num_contacts: false,
                        interactions: true,
                        email: true,
                        timeline: true,
                        accountSummary: false,
                      }
                    : {
                        ...columnVisibility,
                        accountRelationshipType: false,
                        flightRisk: isFlightRiskEnabled ? (columnVisibility.flightRisk ?? true) : false,
                        timeline: isCustomerView ? accountViewMode === "timeline" : true,
                        accountSummary: isSummaryActive,
                      }
                }
                bodyRow={(isCustomerView && newAccountPage) || isSpacesView ? 46 : 35}
                headerRow={(isCustomerView && newAccountPage) || isSpacesView ? 40 : 38}
                pageSize={pageSize}
                timeRange={""} 
                calendarId={null}
                pinnedColumns={isSpacesView ? { left: ["account", "accountRelationshipType"] } : pinnedColumns}
                tablePadding={(isCustomerView && newAccountPage) || isSpacesView ? "0px" : "10px"}
                initialSort={(isCustomerView || isSpacesView) ? initialSort : initialSorting}
                onSortingChange={onSortingChange}
                showVerticalDividers={(isCustomerView && newAccountPage) || isSpacesView ? true : false}
                showSpinner ={showSpinner}
                autoRowHeight={isSummaryActive}
            />
            </Box>
        <SlideOver isOpen={slideOverOpen} onClose={() =>{
            setSlideOverOpen(false);
            setSlideOverType("CLIENT");
            setInitialThemeCategory(undefined);
            sessionStorage.removeItem("selectedClient");
            setSalesforceAccountUrl("");
            setSalesforceAccountId("");
            navigate(location.pathname, {
              replace: true,
              state: null,
            });
        }}
        padding ={slideOverType === "ASK7G" ? "0px": ""}
        >
        {slideOverType === "CLIENT" && (
        <ClientInteractionActionView
            clientDomain={clientDomain}
            isSlideOverOpen={slideOverOpen}
            slideOverParent={slideOverParent}
            parentPageIdentifier={parentPageIdentifier}
            relationshipType={isSpacesView && selectedAccountRelationshipType ? selectedAccountRelationshipType : relationshipType}
            customDateRange={memoCustomDateRange}
            userDetails={ {
              userEmail: "",
              userProfileOrgId: selectedUserPorOrgId
            } }
            clientOrgId={orgId}
            subOrgId={subOrgId}
            initialTab={
              initialThemeCategory
                ? "themes"
                : location.state?.clientOverviewTab ||
                  location.state?.initialTab ||
                  location.state?.activeView
            }
            initialThemeCategory={initialThemeCategory}
            clientName={clientName}
            isSpaceMode={isSpaceMode}
            spaceId={selectedSpace}
            accountViewState={accountViewState}
            appliedFilters={columnFilters}
            salesforceAccountUrl={salesforceAccountUrl}
            salesforceAccountId={salesforceAccountId}
        />
        )}

        {slideOverType === "ASK7G" && (
        <InteractionAsk7G 
        suggestions={[
            `Provide me open action items for ${formatClientDomain(clientDomain)}`,
            "What are the key initiatives for the upcoming quarter?",
            `What are the different products that ${formatClientDomain(clientDomain)} offers`
          ]}    
        variant="clientLevel"     
        clientDoamin={clientDomain}   
        orgId={orgId}
        subOrgId={subOrgId}
        levelAccessType={isSpaceMode ? "client" : "hierarchy"}
        />
        )}
        </SlideOver>
        {isFlightRiskEnabled && (
          <>
            <NavigationSlideOver
              isOpen={flightRiskSlideOverOpen}
              onClose={() => {
                setFlightRiskSlideOverOpen(false);
                setFlightRiskTimestamp(null);
                setFlightRiskAccount(null);
                setFlightRiskHeaderActions(null);
                setIsFlightRiskEditing(false);
                setIsStatusDropdownOpen(false);
                setIsResolveModalOpen(false);
              }}
              parentPage="Accounts"
              subPage={flightRiskAccount?.isCreate ? "Create Flight Risk" : "Flight Risk Assessment"}
              title={capitalizeFirstLetter(flightRiskAccount?.accountName ?? "")}
              badge={
                flightRiskAccount?.isCreate ? (
                  <span className="accounts_flight_risk_slidover_title">
                    <img src={flagRiskIcon} alt="" style={{ width: 13, height: 13 }} />
                    New · Flight Risk
                  </span>
                ) : isFlightRiskResolveEnabled && !isFlightRiskEditing ? (
                  <div className="flight-risk-badge-dropdown-wrap" ref={statusDropdownRef}>
                    <button
                      type="button"
                      className={`accounts_flight_risk_slidover_title accounts_flight_risk_dropdown_trigger${isStatusDropdownOpen ? " accounts_flight_risk_dropdown_trigger--open" : ""}`}
                      onClick={() => setIsStatusDropdownOpen((prev) => !prev)}
                      aria-haspopup="true"
                      aria-expanded={isStatusDropdownOpen}
                    >
                      <img src={flagRiskIcon} alt="" style={{ width: 13, height: 13 }} />
                      Flagged · Flight Risk
                      <svg
                        width="12"
                        height="12"
                        viewBox="0 0 12 12"
                        fill="none"
                        className={`flight-risk-dropdown-chevron${isStatusDropdownOpen ? " flight-risk-dropdown-chevron--open" : ""}`}
                      >
                        <path
                          d="M2.5 4.5L6 8L9.5 4.5"
                          stroke="#991B1B"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </button>

                    {isStatusDropdownOpen && (
                      <div className="flight-risk-status-menu">
                        <div className="flight-risk-status-menu-header">CHANGE STATUS</div>
                        <div
                          className="flight-risk-status-option"
                          onClick={() => {
                            setIsStatusDropdownOpen(false);
                            setIsResolveModalOpen(true);
                          }}
                          role="button"
                          tabIndex={0}
                        >
                          <div className="flight-risk-status-option-icon">
                            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                              <path
                                d="M16.25 5L7.5 13.75L3.75 10"
                                stroke="#158F4C"
                                strokeWidth="2.5"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                          </div>
                          <div className="flight-risk-status-option-content">
                            <span className="flight-risk-status-option-title">Resolved</span>
                            <span className="flight-risk-status-option-desc">
                              Issue addressed, no longer a flight risk
                            </span>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <span className="accounts_flight_risk_slidover_title">
                    <img src={flagRiskIcon} alt="" style={{ width: 13, height: 13 }} />
                    Flagged · Flight Risk
                  </span>
                )
              }
              headerMeta={
                flightRiskTimestamp
                  ? `Timestamp  · ${formatDateWithSlash(flightRiskTimestamp)}`
                  : ""
              }
              headerActions={flightRiskHeaderActions}
              hideCloseButton={isFlightRiskEditing || Boolean(flightRiskAccount?.isCreate)}
            >
              {flightRiskSlideOverOpen && flightRiskAccount && (
              <FlightRiskAssessment
                key={`${flightRiskAccount.orgId}-${flightRiskAccount.subOrgId}-${flightRiskAccount.isCreate ? "create" : "edit"}`}
                orgId={flightRiskAccount.orgId}
                subOrgId={flightRiskAccount.subOrgId}
                setFlightRiskTimestamp={setFlightRiskTimestamp}
                isCreateMode={flightRiskAccount.isCreate}
                accountManagerName={flightRiskAccount.accountManagerName}
                isFlightRiskCreateUpdateEnabled={isFlightRiskCreateUpdateEnabled}
                onHeaderActionsChange={setFlightRiskHeaderActions}
                onEditModeChange={setIsFlightRiskEditing}
                onCreateSuccess={() => {
                  // Flip isCreate → false so the component re-mounts in view
                  // mode and loads the freshly-created record.
                  setIsFlightRiskEditing(false);
                  setFlightRiskAccount((prev) =>
                    prev ? { ...prev, isCreate: false } : null,
                  );
                }}
                onClose={() => {
                  setFlightRiskSlideOverOpen(false);
                  setFlightRiskTimestamp(null);
                  setFlightRiskAccount(null);
                  setFlightRiskHeaderActions(null);
                  setIsFlightRiskEditing(false);
                  setIsStatusDropdownOpen(false);
                  setIsResolveModalOpen(false);
                }}
              />
              )}
            </NavigationSlideOver>

            <MarkAsResolvedModal
              isOpen={isResolveModalOpen}
              accountName={capitalizeFirstLetter(flightRiskAccount?.accountName ?? "")}
              isLoading={isResolving}
              onClose={() => setIsResolveModalOpen(false)}
              onConfirm={handleConfirmResolved}
            />
          </>
        )}
        <Popper
          open={Boolean(timelineAnchorEl)}
          anchorEl={timelineAnchorEl}
          popperRef={timelinePopperRef}
          placement={timelinePlacement}
          popperOptions={{
            strategy: "fixed",
          }}
          modifiers={[
            {
              name: "offset",
              options: {
                offset: [0, 8],
              },
            },
            {
              name: "flip",
              enabled: true,
              options: {
                altBoundary: true,
                rootBoundary: "viewport",
                fallbackPlacements:
                  timelinePlacement === "bottom-start"
                    ? ["top-start", "top-end", "bottom-end"]
                    : ["bottom-start", "bottom-end", "top-end"],
              },
            },
            {
              name: "preventOverflow",
              options: {
                altAxis: true,
                altBoundary: true,
                rootBoundary: "viewport",
                boundary: "viewport",
                padding: 8,
                tether: false,
              },
            },
          ]}
        >
          <ClickAwayListener
            onClickAway={(event) => {
              const target = event.target as HTMLElement;
              if (target.closest('[data-timeline-dot="true"]')) {
                return;
              }
              setTimelineAnchorEl(null);
              setActiveTimelineItem(null);
            }}
          >
            <Box
              ref={timelineTooltipContentRef}
              sx={{
                backgroundColor: "white",
                maxHeight: "calc(100vh - 16px)",
                overflowY: "auto",
                overflowX: "hidden",
                boxShadow: "0px 4px 6px rgba(0, 0, 0, 0.1)",
              }}
            >
              {activeTimelineItem && (
                activeTimelineItem.mode === "mixed" ? (
                  <CombinedTooltip
                    mode={activeTimelineItem.mode}
                    interactions={activeTimelineItem.interactions}
                    emailIds={activeTimelineItem.emailIds}
                    navigationState={activeTimelineItem.navigationState}
                    groupDateKey={activeTimelineItem.groupDateKey}
                    anchorInteractionId={activeTimelineItem.anchorInteractionId}
                  />
                ) : activeTimelineItem.interactions[0]?.type === "email" ? (
                  <EmailTooltip
                    mode={activeTimelineItem.mode}
                    interactions={activeTimelineItem.interactions}
                    emailIds={activeTimelineItem.emailIds}
                  />
                ) : (
                  <InteractionTooltip
                    mode={activeTimelineItem.mode}
                    interactions={activeTimelineItem.interactions}
                    navigationState={activeTimelineItem.navigationState}
                    groupDateKey={activeTimelineItem.groupDateKey}
                    anchorInteractionId={activeTimelineItem.anchorInteractionId}
                  />
                )
              )}
            </Box>
          </ClickAwayListener>
        </Popper>
        </Box>
      </ExclusiveColumnFilterContext.Provider>
    )
}
export default Accounts;
